import { useSessionTransition } from '@/stores/sessionTransition';
import { cookieSessionTransport } from '@/lib/session-transport';
import { beginSessionRecovery, sessionPaused, sessionRealm, useSessionRecovery } from '@/stores/sessionRecovery';
import type { QueryClient } from '@tanstack/react-query';
import { api, waitForSessionRefresh } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import type { User } from '@valiosascrm/shared';

export class SupersededSessionError extends Error {
  constructor() { super('A sessão foi alterada durante a operação.'); }
}

export async function finishCookieLogin(
  queryClient: QueryClient,
  expectedTenantId?: string,
  transition?: { assertCurrent: () => void; expectedUserId?: string; expectedRealm?: string },
) {
  const revision = useAuthStore.getState().sessionRevision;
  const assertCurrent = () => {
    transition?.assertCurrent();
    if (useAuthStore.getState().sessionRevision !== revision) throw new SupersededSessionError();
  };
  // First confirm the cookie identity, then assert that identity for the socket.
  const transport = cookieSessionTransport();
  const profile = await transport.profile();
  assertCurrent();
  let user = profile.data as User;
  if ((expectedTenantId && user?.tenantId !== expectedTenantId) ||
      (transition?.expectedUserId && user?.id !== transition.expectedUserId) ||
      (transition?.expectedRealm && sessionRealm(user) !== transition.expectedRealm)) {
    if (!transition) {
      queryClient.clear();
      useAuthStore.getState().expireSession();
    }
    throw new Error('Não foi possível confirmar a clínica selecionada. Entre novamente.');
  }
  const socket = await transport.socket(user).catch((error) => {
    if (error?.response?.status === 409) throw error;
    return null;
  });
  assertCurrent();
  if (transition) {
    const confirmed = (await transport.profile()).data;
    assertCurrent();
    if (confirmed.id !== user.id || confirmed.tenantId !== user.tenantId || sessionRealm(confirmed) !== sessionRealm(user)) {
      throw new Error('O contexto da sessão mudou durante a confirmação.');
    }
    user = confirmed;
  }
  await queryClient.cancelQueries();
  assertCurrent();
  queryClient.clear();
  useAuthStore.getState().setAuth(user, socket?.data?.token);
  return user;
}

/** Serializes authenticated cookie rotations; navigation completes under the lock. */
export async function changeCookieSession(
  queryClient: QueryClient,
  mutate: () => Promise<unknown>,
  expectedTenantId?: string,
  onComplete?: (user: User) => void,
) {
  const state = useAuthStore.getState();
  if (state.user?.impersonation) throw new Error('Esta alteração não está disponível durante o acesso assistido.');
  if (useSessionTransition.getState().isSwitchingTenant || sessionPaused()) throw new Error('Aguarde a troca de clínica em andamento.');
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    throw new Error('Você está sem conexão. Reconecte para trocar de clínica.');
  }
  const generation = useSessionRecovery.getState().generation;
  let revision = state.sessionRevision;
  const isCurrent = () => useAuthStore.getState().sessionRevision === revision && useSessionRecovery.getState().generation === generation;
  const assertCurrent = () => { if (!isCurrent()) throw new SupersededSessionError(); };
  useSessionTransition.setState({ isSwitchingTenant: true });
  let switched = false;
  try {
    let user: User;
    try {
      await queryClient.cancelQueries();
      await waitForSessionRefresh();
      assertCurrent();
      await mutate();
      assertCurrent();
      switched = true;
      user = await finishCookieLogin(queryClient, expectedTenantId, { assertCurrent, expectedUserId: state.user?.id, expectedRealm: sessionRealm(state.user) });
      revision = useAuthStore.getState().sessionRevision;
    } catch (error) {
      if (!isCurrent()) throw new SupersededSessionError();
      const hasResponse = !!(error as { response?: unknown })?.response;
      const status = (error as { response?: { status?: number } })?.response?.status;
      // No response is ambiguous: cookies may already have rotated on the server.
      if (switched || !hasResponse || status === 401) {
        beginSessionRecovery(state.user);
        throw new SupersededSessionError();
      }
      throw error;
    }
    // A navigation failure must not invalidate an already-confirmed session.
    onComplete?.(user);
    return user;
  } finally {
    if (useAuthStore.getState().sessionRevision === revision) useSessionTransition.setState({ isSwitchingTenant: false });
  }
}

export async function switchTenant(queryClient: QueryClient, tenantId: string, onComplete?: (user: User) => void) {
  if (useAuthStore.getState().user?.impersonation) throw new Error('A troca de clínica não está disponível durante o acesso assistido.');
  // Membership may have been granted since /auth/me. The switch endpoint is
  // authoritative; the profile list only supplies dropdown options.
  return changeCookieSession(queryClient, () => api.post('/auth/switch-clinic', { tenantId }), tenantId, onComplete);
}

/** Logout and ending impersonation share the cookie transition lock. */
export async function endCookieSession(queryClient: QueryClient, endpoint = '/auth/logout') {
  const state = useAuthStore.getState();
  if (state.user?.impersonation) endpoint = '/auth/impersonation/end';
  if (useSessionTransition.getState().isSwitchingTenant || sessionPaused()) return false;
  const revision = state.sessionRevision;
  useSessionTransition.setState({ isSwitchingTenant: true });
  try {
    await queryClient.cancelQueries();
    await waitForSessionRefresh();
    if (useAuthStore.getState().sessionRevision !== revision) return false;
    await api.post(endpoint);
  } catch (error) {
    if ((error as { response?: { status?: number } })?.response?.status === 403 &&
        useAuthStore.getState().sessionRevision === revision) {
      beginSessionRecovery(state.user);
      return false;
    }
    if (state.user?.impersonation && useAuthStore.getState().sessionRevision === revision && !sessionPaused()) {
      throw new Error('Não foi possível confirmar o encerramento do acesso assistido. Tente novamente.');
    }
    // Normal logout can still end the local session when offline.
  } finally {
    if (useAuthStore.getState().sessionRevision === revision) useSessionTransition.setState({ isSwitchingTenant: false });
  }
  if (sessionPaused()) return false;
  if (useAuthStore.getState().sessionRevision !== revision) return false;
  useAuthStore.getState().logout();
  queryClient.clear();
  return true;
}
